import {
  Box,
  Typography,
  Alert,
  Link,
  TextField,
  Button,
  Divider,
  Paper,
} from "@mui/material";
import { radius } from "../../../theme";
import { LoadingButton } from "@mui/lab";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Seo from "../../util/seo";
import {
  signIn,
  signInWithLink,
  resetPassword,
  signInWithProvider,
} from "../../../providers/usuarioProvider";
import { useThemeMode } from "../../../providers/themeModeContext";
import { FirebaseError } from "firebase/app";
import {
  getAuth,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  GoogleAuthProvider,
} from "firebase/auth";
import logoAzul from "../../../assets/logo_azul_horizontal.svg";
import logoBranca from "../../../assets/logo_branca_horizontal.svg";
import { IconBrandGoogle } from "@tabler/icons-react";

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginSenha, setLoginSenha] = useState("");
  const [isMagicLinkUrl, setIsMagicLinkUrl] = useState(false);
  const [message, setMessage] = useState<{
    text: string;
    type: "success" | "warning" | "error";
  } | null>(null);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const themeMode = useThemeMode();

  useEffect(() => {
    // Verifica se a URL atual é um link de magic link
    setIsMagicLinkUrl(isSignInWithEmailLink(getAuth(), window.location.href));
  }, []);

  const handleSendMagicLink = async () => {
    if (!loginEmail) {
      setMessage({
        text: "Digite seu email para receber o link de login",
        type: "warning",
      });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const actionCodeSettings = {
        url: window.location.origin + "/login",
        handleCodeInApp: true,
      };

      await sendSignInLinkToEmail(getAuth(), loginEmail, actionCodeSettings);
      setMessage({
        text: "Um link de login foi enviado para seu email. Verifique sua caixa de entrada.",
        type: "success",
      });
    } catch (error: any) {
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case "auth/invalid-email":
            setMessage({ text: "E-mail inválido", type: "warning" });
            break;
          case "auth/user-disabled":
            setMessage({ text: "Usuário não existe", type: "warning" });
            break;
          default:
            const errorMessage =
              "Erro ao enviar link de login. Tente novamente";
            setMessage({
              text: errorMessage,
              type: "error",
            });
            setError(errorMessage);
        }
      } else {
        const errorMessage = "Erro ao enviar link de login. Tente novamente";
        setMessage({
          text: errorMessage,
          type: "error",
        });
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setMessage(null);
    setError("");

    try {
      // Se é uma URL de magic link, usa signInWithLink
      if (isMagicLinkUrl) {
        await signInWithLink(loginEmail);
        navigate("/painel");
      } else {
        // Se não é magic link, usa signIn normal com senha
        await signIn(loginEmail, loginSenha).then(() => navigate("/painel"));
      }
    } catch (error: any) {
      if (error instanceof FirebaseError) {
        switch (error.code) {
          case "auth/invalid-credential":
            setMessage({ text: "Email ou senha incorretos", type: "warning" });
            break;
          case "auth/user-not-found":
            setMessage({ text: "Usuário não encontrado", type: "warning" });
            break;
          case "auth/wrong-password":
            setMessage({ text: "Senha incorreta", type: "warning" });
            break;
          case "auth/too-many-requests":
            setMessage({
              text: "Muitas tentativas. Tente novamente mais tarde",
              type: "warning",
            });
            break;
          case "auth/expired-action-code":
            setMessage({
              text: "Este link expirou. Solicite outro link ou redefina sua senha.",
              type: "warning",
            });
            break;
          case "auth/invalid-email":
            setMessage({
              text: "E-mail inválido. Coloque o mesmo e-mail que foi cadastrado.",
              type: "warning",
            });
            break;
          case "auth/user-disabled":
            setMessage({ text: "Usuário não existe", type: "warning" });
            break;
          default:
            const errorMessage = "Erro ao fazer login. Tente novamente";
            setMessage({
              text: errorMessage,
              type: "error",
            });
            setError(errorMessage);
        }
      } else {
        const errorMessage = "Erro ao fazer login. Tente novamente";
        setMessage({
          text: errorMessage,
          type: "error",
        });
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = () => {
    if (!loginEmail) {
      setMessage({
        text: "Digite seu email para redefinir a senha",
        type: "warning",
      });
    } else {
      resetPassword(loginEmail).then(() => {
        setMessage({
          text: "Um email com um link para a recuperação de senha foi enviado para você.",
          type: "success",
        });
      });
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setMessage(null);
    setError("");

    try {
      const provider = new GoogleAuthProvider();
      await signInWithProvider(provider, {});
      navigate("/painel");
    } catch (error: any) {
      if (error instanceof FirebaseError) {
        const errorMessage = "Erro ao fazer login com Google. Tente novamente";
        setMessage({
          text: errorMessage,
          type: "error",
        });
        setError(errorMessage);
      } else {
        const errorMessage = "Erro ao fazer login com Google. Tente novamente";
        setMessage({
          text: errorMessage,
          type: "error",
        });
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <Seo
        title="Felix na Bolsa"
        titleTemplate="%s · Entrar"
        description="Faça login na sua conta Felix na Bolsa."
      />
      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
          // Em telas de ~720-768px de altura o formulário não cabe em 100vh.
          // No desktop travamos a altura e deixamos a COLUNA do formulário rolar
          // por dentro, para a página nunca ficar mais alta que a viewport.
          // No mobile deixamos fluir: rolagem de página ali é o esperado.
          height: { md: "100vh" },
          overflow: { md: "hidden" },
          backgroundColor: "background.default",
        }}
      >
        {/* Seção Esquerda - Editorial: eyebrow + display com palavra em itálico
            accent. Sem gradiente: a personalidade vem da tipografia. */}
        <Box
          sx={{
            flex: 1,
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "center",
            borderRight: 1,
            borderColor: "divider",
            px: { md: 6, lg: 12 },
            py: 8,
          }}
        >
          <Box sx={{ maxWidth: 560 }}>
            <Typography
              variant="eyebrow"
              color="text.secondary"
              sx={{ display: "block", mb: 3 }}
            >
              Felix na Bolsa
            </Typography>
            <Divider
              sx={{
                mb: 4,
                maxWidth: 64,
                borderBottomWidth: 2,
                borderColor: "accentInk",
              }}
            />
            <Typography
              variant="display"
              component="h2"
              sx={{ mb: 4, color: "text.primary" }}
            >
              Controle Fiscal{" "}
              <Box
                component="span"
                sx={{ fontStyle: "italic", color: "accentInk" }}
              >
                Descomplicado
              </Box>
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                fontSize: "1.0625rem",
                lineHeight: 1.6,
                maxWidth: 460,
              }}
            >
              Solução inteligente para o cálculo de Imposto de Renda em
              investimentos na Bolsa, liberando você para focar no crescimento
            </Typography>
          </Box>
        </Box>

        {/* Seção Direita - Formulário de Login */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: { xs: 2, md: 6, lg: 10 },
            // py enxuto de proposito: com py:8 o conteudo dava 898px e estourava
            // qualquer tela de 768px ou menos.
            py: { xs: 3, md: 4 },
            overflowY: { md: "auto" },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: 480,
              mx: "auto",
              backgroundColor: "background.paper",
              border: 1,
              borderColor: "divider",
              borderRadius: `${radius.xl}px`,
              p: { xs: 3, md: 3 },
            }}
          >
            {/* Logo e Mensagem alinhadas à esquerda */}
            <Box sx={{ mb: 3 }}>
              <Box
                component="img"
                src={themeMode === "light" ? logoAzul : logoBranca}
                alt="Felix na Bolsa"
                sx={{
                  height: 40,
                  mb: 2,
                  display: "block",
                }}
              />
              <Typography
                variant="eyebrow"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Entrar
              </Typography>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{ color: "text.primary" }}
              >
                Bem-vindo de volta
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Faça login na sua conta
              </Typography>
            </Box>
            <Box>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                {message && (
                  <Alert severity={message.type} sx={{ mb: 2 }}>
                    {message.text}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  margin="normal"
                  required
                  autoComplete="email"
                />

                {/* Campo de senha só aparece se NÃO for magic link */}
                {!isMagicLinkUrl && (
                  <Box textAlign="end">
                    <TextField
                      fullWidth
                      label="Senha"
                      type="password"
                      value={loginSenha}
                      onChange={(e) => setLoginSenha(e.target.value)}
                      margin="normal"
                      required
                      autoComplete="current-password"
                    />
                    <Link
                      onClick={handleResetPassword}
                      variant="body2"
                      underline="hover"
                      sx={{
                        cursor: "pointer",
                        color: "accentInk",
                        fontWeight: 500,
                      }}
                    >
                      Esqueceu sua senha?
                    </Link>
                  </Box>
                )}

                {/* Botão de login - comportamento diferente baseado no contexto */}
                {!isMagicLinkUrl ? (
                  <Box>
                    <LoadingButton
                      type="submit"
                      fullWidth
                      variant="contained"
                      loading={loading}
                      sx={{ mt: 2.5, mb: 1.5, py: 1.25 }}
                      size="large"
                    >
                      Entrar
                    </LoadingButton>

                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleSendMagicLink}
                      disabled={loading}
                      sx={{ mb: 1.5, py: 1.25 }}
                    >
                      Receber link de login por email
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <Alert severity="info" sx={{ mb: 2 }} icon={false}>
                      <Typography
                        variant="labelMono"
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        Login por Email
                      </Typography>
                      <Typography variant="body2">
                        Você recebeu um link de login por email. Digite seu
                        email abaixo para acessar sua conta.
                      </Typography>
                    </Alert>

                    <LoadingButton
                      type="submit"
                      fullWidth
                      variant="contained"
                      loading={loading}
                      sx={{ mt: 1.5, mb: 1.5, py: 1.25 }}
                      size="large"
                    >
                      Entrar com Email
                    </LoadingButton>
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                  color="secondary"
                  sx={{ mb: 1.5, py: 1.25 }}
                  startIcon={<IconBrandGoogle />}
                >
                  Entrar com Google
                </Button>
              </Box>
            </Box>
            <Box>
              <Box mt={3} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  Não tem uma conta?{" "}
                  <Link
                    href="/registrar"
                    underline="hover"
                    sx={{ color: "accentInk", fontWeight: 500 }}
                  >
                    Criar conta
                  </Link>
                </Typography>
              </Box>

              <Box mt={3} textAlign="center">
                <Typography variant="body2" color="text.secondary">
                  Ao continuar, você concorda com nossos{" "}
                  <Link
                    href="/termos"
                    underline="hover"
                    sx={{ color: "accentInk", fontWeight: 500 }}
                  >
                    Termos de Uso
                  </Link>{" "}
                  e{" "}
                  <Link
                    href="/privacidade"
                    underline="hover"
                    sx={{ color: "accentInk", fontWeight: 500 }}
                  >
                    Política de Privacidade
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    </main>
  );
}
